'use client';

import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useWindowSize } from 'react-use-v13';
import { Icon } from '@scale/scaleui';
import { TextCollectionParams } from '@scale/shared/types/params/textcollection';
import { TextCollectionResponse } from '@scale/shared/types/response/textcollection';
import { ResetGlobalStyles } from './TempComponents';
import { ModelResponseCentricColumn, transformToResponseCentricView } from './audit/transform';
import { COLUMN_FREEZE_LINE, useAuditLayoutStore } from './hooks/useAuditLayoutStore';
import { AuditLayoutControlPanel } from './audit/AuditLayoutControlPanel';
import { TextCollectionResponseCompact } from './definitions/TextCollectionResponseCompact';
import { ColumnHeader } from './audit/ColumnHeader';
import { AuditColumn } from './audit/AuditColumn';
import { ResponseRewriteDisplay } from './audit/ResponseRewriteDisplay';
import { MetadataDisplay } from './audit/MetadataDisplay';
import { ExpandableMessageHistory } from './audit/ExpandableMessageHistory';
import { useChatStoreExtract } from '../Chat/hooks/ChatStore.hooks';
import { GlobalStepWidth } from '../Chat/hooks/types';
import { MultiTurnThreadHistory } from './ThreadHistory';
import { getStepDescriptorByType } from './definitions';

interface ChatTaskAuditProps {
  readOnly?: boolean;
}

function ChatResponseAuditColumn({ column }: { column: ModelResponseCentricColumn }) {
  const { columnWidthEx, columnDisplayOrder, sectionVisibility, hideUnselectedResponses } =
    useAuditLayoutStore();
  const [beforeFreezeSections, afterFreezeSections] = useMemo(() => {
    const visibleColumns = columnDisplayOrder.filter(c => sectionVisibility[c]);
    const freezeIndex = visibleColumns.indexOf(COLUMN_FREEZE_LINE);
    const beforeFreeze = visibleColumns.slice(0, freezeIndex);
    const afterFreeze = visibleColumns.slice(freezeIndex + 1);
    return [beforeFreeze, afterFreeze];
  }, [columnDisplayOrder, sectionVisibility]);

  const renderSection = (section: string) => {
    if (section === 'prompt') {
      return <ExpandableMessageHistory messages={column.messages} />;
    } else if (section === 'response') {
      return (
        <ResponseRewriteDisplay
          responseMarkdown={column.response?.content}
          rewriteMarkdown={column.rewrite}
        />
      );
    } else if (section === 'metadata') {
      return <MetadataDisplay metadata={column.metadata} />;
    } else if (section === 'annotations') {
      // TODO(albert): Address type casting in a more elegant way
      return (
        <div>
          {column.annotations.map(([stepParams, response], index) => (
            <TextCollectionResponseCompact
              key={index}
              params={{ ...stepParams.params, attachments: [] } as unknown as TextCollectionParams}
              response={response as unknown as TextCollectionResponse}
            />
          ))}
        </div>
      );
    }
  };

  // Always keep the column height so that it uses as much vertical space as possible.
  const dividerRef = useRef<HTMLDivElement>(null);
  const [unfrozenHeight, setUnfrozenHeight] = useState<number>(800);
  const { height } = useWindowSize();

  useEffect(() => {
    if (!dividerRef.current) {
      return;
    }
    const rect = dividerRef.current.getBoundingClientRect();
    const rectWindowTop = window.scrollY + rect.top;
    setUnfrozenHeight(height - rectWindowTop - 40);
  }, [dividerRef, height, setUnfrozenHeight, sectionVisibility, columnDisplayOrder]);

  if (hideUnselectedResponses && !column.isGoldenPath) {
    return (
      <div className="py-2">
        <Icon
          icon="comment-slash"
          size="sm"
          tooltip={`Unselected column "${column.id}" hidden`}
          hasBackground
        />
      </div>
    );
  }
  return (
    <div className="flex flex-col flex-shrink-0 gap-2" style={{ width: `${columnWidthEx}ex` }}>
      <ColumnHeader title={column.id} isGoldenPath={column.isGoldenPath} isResponseColumn />
      <div className="flex flex-col gap-2">
        {beforeFreezeSections.map(section => renderSection(section))}
      </div>
      <div
        ref={dividerRef}
        className="flex flex-col gap-2 overflow-y-auto border-t border-dashed border-neutral-300 pt-2"
        style={{ height: unfrozenHeight }}
      >
        {afterFreezeSections.map(section => renderSection(section))}
      </div>
    </div>
  );
}

export const ChatTaskAudit: React.FC<ChatTaskAuditProps> = () => {
  const showBefore = useAuditLayoutStore(state => state.showBefore);
  const showAfter = useAuditLayoutStore(state => state.showAfter);

  const config = useChatStoreExtract(state => state.config);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const responses = useChatStoreExtract(state => state.responses);

  const setStepWidth = useChatStoreExtract(state => state.setStepWidth);

  useEffect(() => {
    setStepWidth(GlobalStepWidth.Full);
  }, [setStepWidth]);

  const { before, columns, after } = useMemo(() => {
    const threadHistory = new MultiTurnThreadHistory(config, responses, reviewLevel);
    return transformToResponseCentricView(threadHistory);
  }, [config, responses, reviewLevel]);

  return (
    <ResetGlobalStyles>
      <div className="flex gap-4 pt-8 pl-8 box-border w-screen overflow-x-auto">
        <AuditLayoutControlPanel />
        {showBefore && (
          <AuditColumn>
            <ColumnHeader title="Before Steps" isGoldenPath={true} />
            {before.map(([stepParams, response], stepIndex) => {
              if (!stepParams || !response) return null;
              const step = getStepDescriptorByType(stepParams.type);
              if (!step) return null;
              return (
                <step.ViewComponent
                  key={`before-step-${stepIndex}`}
                  readOnly={true}
                  complete={!!response}
                  responseIndex={response!.index}
                />
              );
            })}
          </AuditColumn>
        )}
        {columns.map(column => (
          <ChatResponseAuditColumn key={column.id} column={column} />
        ))}
        {showAfter && (
          <AuditColumn>
            <ColumnHeader title="After Steps" isGoldenPath={true} />
            {after.map(([stepParams, response], stepIndex) => {
              if (!stepParams || !response) return null;
              const step = getStepDescriptorByType(stepParams.type);
              if (!step) return null;
              return (
                <step.ViewComponent
                  key={`after-step-${stepIndex}`}
                  readOnly={true}
                  complete={!!response}
                  responseIndex={response!.index}
                />
              );
            })}
          </AuditColumn>
        )}
      </div>
    </ResetGlobalStyles>
  );
};
